/**
 * Stats — count numbers up with GSAP when the block enters the viewport.
 */

(function () {
    'use strict';

    const SELECTOR = '[data-stats]';
    const VALUE_SELECTOR = '[data-stat-count]';

    function prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    function waitForGsap(timeoutMs = 8000) {
        if (window.gsap && window.ScrollTrigger) {
            return Promise.resolve({
                gsap: window.gsap,
                ScrollTrigger: window.ScrollTrigger,
            });
        }

        return new Promise((resolve, reject) => {
            const start = Date.now();
            const tick = () => {
                if (window.gsap && window.ScrollTrigger) {
                    resolve({
                        gsap: window.gsap,
                        ScrollTrigger: window.ScrollTrigger,
                    });
                    return;
                }

                if (Date.now() - start >= timeoutMs) {
                    reject(new Error('GSAP not available'));
                    return;
                }

                window.requestAnimationFrame(tick);
            };

            tick();
        });
    }

    function formatValue(value, decimals) {
        if (decimals > 0) {
            return value.toFixed(decimals);
        }

        return String(Math.round(value));
    }

    function setFinalValues(block) {
        block.querySelectorAll(VALUE_SELECTOR).forEach((el) => {
            const target = parseFloat(el.dataset.statTarget || '0');
            const decimals = parseInt(el.dataset.statDecimals || '0', 10) || 0;
            el.textContent = formatValue(
                Number.isFinite(target) ? target : 0,
                decimals
            );
        });
    }

    function animateBlock(block, gsap, ScrollTrigger) {
        if (block.dataset.statsInit === '1') {
            return;
        }

        block.dataset.statsInit = '1';

        const values = Array.from(block.querySelectorAll(VALUE_SELECTOR));
        if (!values.length) {
            return;
        }

        if (prefersReducedMotion()) {
            setFinalValues(block);
            return;
        }

        values.forEach((el) => {
            const target = parseFloat(el.dataset.statTarget || '0');
            const decimals = parseInt(el.dataset.statDecimals || '0', 10) || 0;
            const end = Number.isFinite(target) ? target : 0;
            const state = { value: 0 };

            el.textContent = formatValue(0, decimals);

            gsap.to(state, {
                value: end,
                duration: 1.6,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: block,
                    start: 'top 80%',
                    once: true,
                },
                onUpdate() {
                    el.textContent = formatValue(state.value, decimals);
                },
                onComplete() {
                    el.textContent = formatValue(end, decimals);
                },
            });
        });

        ScrollTrigger.refresh();
    }

    async function initStats(container) {
        const root = container || document;
        const blocks = root.querySelectorAll(SELECTOR);

        if (!blocks.length) {
            return;
        }

        try {
            const { gsap, ScrollTrigger } = await waitForGsap();
            blocks.forEach((block) => animateBlock(block, gsap, ScrollTrigger));
        } catch (error) {
            blocks.forEach((block) => setFinalValues(block));

            if (window.sojTheme?.debug) {
                // eslint-disable-next-line no-console
                console.warn('Stats: GSAP count-up unavailable', error);
            }
        }
    }

    function onPageReady(e) {
        const container = e?.detail?.container || document;
        if (container.querySelector(SELECTOR)) {
            initStats(container);
        }
    }

    if (document.body?.hasAttribute?.('data-barba')) {
        document.addEventListener('barba:pageReady', onPageReady);
    } else if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () =>
            onPageReady({ detail: { container: document } })
        );
    } else {
        onPageReady({ detail: { container: document } });
    }
})();
